import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { ModalComponent, ToastrService } from '@kiwid-app/kiwid-ui-lib-angular';
import { IError } from '../models/error/error';
import { EStatusCode } from 'app/core/enums/status-code.enum';

@Injectable()
export class ErrorV2Helper {
    static setFormError(form: FormGroup, error: IError, toastr: ToastrService | ModalComponent, excludes?: string[]): void {
        let message = ErrorV2Helper.errorMessage(error);
        const errorFields = this.parseErrorFieldsNotForm(error);
        const errorWithoutField = this.parseErrorInForm(form, error, toastr, excludes);
        if (errorWithoutField.length > 0) {
            if (!!message) {
                errorWithoutField.unshift(message);
            }
            message = errorWithoutField.join('<br>');
        }

        if (!!message) {
            if (toastr instanceof ModalComponent) {
                toastr?.showToastr({ message, type: 'error' });
                return;
            }

            toastr?.show({ message, type: 'error' });
        }

        form.markAllAsTouched();
    }

    static parseErrorInForm(form: FormGroup, error: IError, toastr: ToastrService | ModalComponent, excludes?: string[]): string[] {
        if (!!error.fields && error.fields.length > 0 && !!form) {
            const errorWithoutField: string[] = [];
            for (const field of error.fields) {
                if (!!excludes && excludes.includes(field.field)) {
                    errorWithoutField.push(`${field.field}: ${field.error}`);
                    continue;
                }

                const control = form.get(field.field);
                if (!control) {
                    errorWithoutField.push(`${field.field}: ${field.error}`);
                    continue;
                }

                if (!!control && !!field.children && field.children.length > 0 && control instanceof FormGroup) {
                    for (const children of field.children) {
                        if (!!excludes && excludes.includes(`${field.field}.${children.field}`)) {
                            errorWithoutField.push(`${children.field}: ${children.error}`);
                            continue;
                        }

                        if (children.children?.length ?? 0 > 0) {
                            const newError: IError = {
                                error: '',
                                message: '',
                                path: '',
                                statusCode: 0,
                                timestamp: '',
                                fields: [children],
                            };
                            const childrenErrors = this.parseErrorInForm(control.get(children.field) as FormGroup, newError, toastr, excludes);
                            errorWithoutField.push(...childrenErrors);
                            continue;
                        }

                        const childrenControl = control.get(children.field);
                        if (!!childrenControl && childrenControl instanceof FormControl) {
                            childrenControl.setErrors({ message: children.error });
                        }
                    }
                }

                if (!!control && control instanceof FormControl) {
                    control.setErrors({ message: field.error });
                }

                if (!!control && control instanceof FormArray && !!field.children && field.children.length > 0) {
                    field.children.forEach((childrenField, index) => {
                        if (!!childrenField.children && childrenField.children.length > 0) {
                            childrenField.children?.forEach((children, iChildren) => {
                                const newError: IError = {
                                    error: '',
                                    message: '',
                                    path: '',
                                    statusCode: 0,
                                    timestamp: '',
                                    fields: [children],
                                };
                                this.setFormError(control.controls[index] as FormGroup, newError, toastr);
                            });
                        }

                        if (!!childrenField.error) {
                            control.controls[index].setErrors({ message: childrenField.error });
                        }
                    });
                }
            }
            return errorWithoutField;
        }

        return [];
    }

    static parseErrorFieldsNotForm(error: IError): string[] {
        if (!!error && !!error.fields && error.fields.length > 0) {
            const errorWithoutField: string[] = [];
            for (const field of error.fields) {
                errorWithoutField.push(`${field.field}: ${field.error}`);

                if (!!field.children && field.children.length > 0) {
                    field.children.forEach((childrenField, index) => {
                        if (!!childrenField.children && childrenField.children.length > 0) {
                            childrenField.children?.forEach((children, iChildren) => {
                                this.parseErrorFieldsNotForm({
                                    error: '',
                                    message: '',
                                    path: '',
                                    statusCode: 0,
                                    timestamp: '',
                                    fields: [children],
                                });
                            });
                        }

                        if (!!childrenField.error) {
                            errorWithoutField.push(`${field.field}[${index}]: ${field.error}`);
                        }
                    });
                }
            }
            return errorWithoutField;
        }

        return [];
    }

    private static isExcludeInField(excludes: string[], field: string): boolean {
        excludes.forEach((exclude) => {
            if (exclude.indexOf('.') > 0) {
                const keys = exclude.split('.');
                if (keys) {
                    if (keys.includes(field)) {
                        return true;
                    }
                }
            } else {
                if (excludes.includes(field)) {
                    return true;
                }
            }
        });

        return false;
    }

    static setToastrError(toastrService: ToastrService, error: any): void {
        let message = ErrorV2Helper.errorMessage(error);
        const errorFields = this.parseErrorFieldsNotForm(error);

        if (errorFields.length > 0) {
            if (!!message) {
                errorFields.unshift(message);
            }

            message = errorFields.join('<br>');
        }

        if (!message) {
            return;
        }

        toastrService.show({ message, type: 'error' });
    }

    static setToastrErrorModal(toastr: ModalComponent, error: any): void {
        let message = ErrorV2Helper.errorMessage(error);
        const errorFields = this.parseErrorFieldsNotForm(error);

        if (errorFields.length > 0) {
            if (!!message) {
                errorFields.unshift(message);
            }

            message = errorFields.join('<br>');
        }

        if (!message) {
            return;
        }

        toastr.showToastr({ message, type: 'error' });
    }

    private static errorMessage(error: any): string | null {
        if ([403, 404, 490, 491, 498, 499, 500].includes(error?.statusCode)) {
            return null;
        }

        if (error?.statusCode === 401 && !error?.path.includes('auth/sign-in')) {
            return null;
        }

        if (typeof error === 'string') {
            return error;
        }

        return error?.message;
    }
}
